import {
  collection,
  doc,
  setDoc,
  getDocs,
  deleteDoc,
  query,
  where,
  serverTimestamp,
  addDoc,
  Timestamp
} from 'firebase/firestore';
import { db } from './config';

// Helper function to format date in local timezone (YYYY-MM-DD)
const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper function to format time in local timezone (HH:mm)
const formatLocalTime = (date) => {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

// Use the shared events collection (same as calendar-app)
const getEventsCollection = () => {
  return collection(db, 'events');
};

// Create a new event (compatible with calendar-app format)
export const createEvent = async (userId, event) => {
  try {
    const eventsRef = getEventsCollection();

    // Convert to calendar-app format with Timestamps
    const startDateTime = new Date(`${event.date}T${event.startTime || '00:00'}`);
    // Use endDate if provided (for multi-day events), otherwise use same date
    const endDate = event.endDate || event.date;
    const endDateTime = new Date(`${endDate}T${event.endTime || '23:59'}`);

    const docRef = await addDoc(eventsRef, {
      title: event.title,
      description: event.description || '',
      startTime: Timestamp.fromDate(startDateTime),
      endTime: Timestamp.fromDate(endDateTime),
      color: event.color || '#3b82f6', // blue color as default
      type: event.type || 'other',
      createdBy: userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error creating event:', error);
    return { success: false, error };
  }
};

// Update an event (compatible with calendar-app format)
export const updateEvent = async (userId, eventId, eventData) => {
  try {
    const eventRef = doc(db, 'events', eventId);

    // Convert to calendar-app format with Timestamps
    const updateData = {};

    if (eventData.title) updateData.title = eventData.title;
    if (eventData.description !== undefined) updateData.description = eventData.description;
    if (eventData.color) updateData.color = eventData.color;
    if (eventData.type) updateData.type = eventData.type;

    if (eventData.date || eventData.startTime || eventData.endTime) {
      const startDateTime = new Date(`${eventData.date}T${eventData.startTime || '00:00'}`);
      // Use endDate if provided (for multi-day events), otherwise use same date
      const endDate = eventData.endDate || eventData.date;
      const endDateTime = new Date(`${endDate}T${eventData.endTime || '23:59'}`);
      updateData.startTime = Timestamp.fromDate(startDateTime);
      updateData.endTime = Timestamp.fromDate(endDateTime);
    }

    updateData.updatedAt = serverTimestamp();

    await setDoc(eventRef, updateData, { merge: true });
    return { success: true };
  } catch (error) {
    console.error('Error updating event:', error);
    return { success: false, error };
  }
};

// Delete an event
export const deleteEvent = async (userId, eventId) => {
  try {
    const eventRef = doc(db, 'events', eventId);
    await deleteDoc(eventRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting event:', error);
    return { success: false, error };
  }
};

// Get all events (calendar-app style - all events from the shared collection)
export const getEvents = async (userId) => {
  try {
    const eventsRef = getEventsCollection();
    const querySnapshot = await getDocs(eventsRef);
    const events = [];
    querySnapshot.forEach((docSnap) => {
      const data = docSnap.data();
      // Convert Firestore Timestamps to dates for display
      const eventData = {
        id: docSnap.id,
        ...data
      };

      // Handle date conversion if needed
      if (data.startTime && data.startTime.toDate) {
        const startDateTime = data.startTime.toDate();
        eventData.date = formatLocalDate(startDateTime);
        eventData.startTime = formatLocalTime(startDateTime);
      }
      if (data.endTime && data.endTime.toDate) {
        const endDateTime = data.endTime.toDate();
        eventData.endDate = formatLocalDate(endDateTime);
        eventData.endTime = formatLocalTime(endDateTime);
      }

      events.push(eventData);
    });
    return { success: true, events };
  } catch (error) {
    console.error('Error getting events:', error);
    return { success: false, error };
  }
};

// Get events for a specific date range
export const getEventsByDateRange = async (userId, startDate, endDate) => {
  try {
    const eventsRef = getEventsCollection();
    const q = query(
      eventsRef,
      where('date', '>=', startDate),
      where('date', '<=', endDate)
    );
    const querySnapshot = await getDocs(q);
    const events = [];
    querySnapshot.forEach((docSnap) => {
      const data = docSnap.data();
      events.push({ id: docSnap.id, ...data });
    });
    return { success: true, events };
  } catch (error) {
    console.error('Error getting events by date range:', error);
    return { success: false, error };
  }
};
